import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import NotFound from "./NotFound";
import { useSEO } from "../lib/useSEO";
import { SERVICES, FAQS, OHIO_CITIES, SERVICED_STATES_SCHEMA } from "../data/content";
import { ACCIDENTS } from "../data/accidents";
import { ArrowRight, CheckCircle2, Scale, Users, Briefcase, Heart, HelpCircle, MapPin } from "lucide-react";

const SITE_URL = "https://instabridgefunding.com";

const ICONS = {
  "pre-settlement": Users,
  "attorney-lending": Briefcase,
  "litigation-finance": Scale,
  "medical-funding": Heart,
};

const DETAILS = {
  "pre-settlement": {
    audience_label: "For plaintiffs",
    bullets: [
      "Non-recourse — repay only from your settlement",
      "No credit check, no SSN, no income verification",
      "Funded in 24–48 hours after attorney verification",
      "Capped advances that protect your case",
      { text: "Available in 9 states — see where we serve", to: "/states" },
    ],
    eligibility: ["Active personal-injury case with retained counsel", "Case has identifiable defendant or insurance coverage", "You're 18+ and a US resident"],
    cta_primary: { label: "Apply for funding", to: "/apply" },
    cta_secondary: { label: "How it works", to: "/how-it-works" },
  },
  "attorney-lending": {
    audience_label: "For law firms",
    bullets: [
      "Working-capital lines of credit",
      "Term loans for growth, payroll, or marketing",
      "Built around contingency-fee revenue and case timing",
      "No cookie-cutter terms — every deal structured by an attorney",
      "Confidential and discreet — no public liens",
    ],
    eligibility: ["Established plaintiff law firm (2+ years)", "Active contingency-fee case inventory", "Verifiable settlement track record"],
    cta_primary: { label: "Schedule a call", to: "/contact" },
    cta_secondary: { label: "For attorneys overview", to: "/for-attorneys" },
  },
  "litigation-finance": {
    audience_label: "For complex cases",
    bullets: [
      "Case-cost financing for high-value litigation",
      "Funds experts, depositions, trial prep, and trial costs",
      "Structured around the case timeline and risk profile",
      "Non-recourse — paid back from the recovery only",
      "Attorney-led underwriting on every deal",
    ],
    eligibility: ["High-value commercial or mass-tort litigation", "Substantial expected recovery (typically $1M+)", "Documented case strategy and milestones"],
    cta_primary: { label: "Schedule a call", to: "/contact" },
    cta_secondary: { label: "For attorneys overview", to: "/for-attorneys" },
  },
  "medical-funding": {
    audience_label: "For plaintiffs & providers",
    bullets: [
      "Letters of protection for treating providers",
      "Direct medical-lien funding so treatment isn't delayed",
      "Paid back from the case settlement",
      "Coordinated with your attorney's office",
      "No out-of-pocket cost to plaintiffs",
    ],
    eligibility: ["Active personal-injury case", "Required medical treatment is reasonable and case-related", "Provider willing to work on lien basis"],
    cta_primary: { label: "Get medical funding", to: "/apply" },
    cta_secondary: { label: "Talk to our team", to: "/contact" },
  },
};

export default function Services() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useSEO(
    service ? service.name : "Service not found",
    service ? service.long.slice(0, 158) : "",
    service
      ? {
          canonical: `${SITE_URL}/services/${service.slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                name: service.name,
                serviceType: service.schema_service_type || service.name,
                description: service.long_intro || service.long,
                provider: { "@type": "Organization", name: "Instabridge Funding" },
                areaServed: service.slug === "pre-settlement" ? SERVICED_STATES_SCHEMA : { "@type": "Country", name: "United States" },
              },
              service.faqs && service.faqs.length ? {
                "@type": "FAQPage",
                mainEntity: service.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              } : null,
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services/${service.slug}` },
                  { "@type": "ListItem", position: 3, name: service.name, item: `${SITE_URL}/services/${service.slug}` },
                ],
              },
            ].filter(Boolean),
          },
        }
      : { noindex: true },
  );

  if (!service) return <NotFound />;

  const detail = DETAILS[service.slug];
  const Icon = ICONS[service.slug] || Briefcase;
  const relatedAccidents = (service.related_accidents || [])
    .map((s) => ACCIDENTS.find((a) => a.slug === s))
    .filter(Boolean);
  const relatedCities = (service.related_cities || [])
    .map((s) => OHIO_CITIES.find((c) => c.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid={`service-page-${service.slug}`}>
      <Navbar />

      <section className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="text-xs text-[#6B7280] mb-5">
            <Link to="/" className="hover:text-[#0A2540]">Home</Link> <span className="mx-2">/</span>
            <span className="text-[#0A2540]">Services</span> <span className="mx-2">/</span>
            <span className="text-[#0A2540]">{service.name}</span>
          </div>
          <div className="flex items-center gap-2 uppercase-label !text-[#0066FF] mb-3"><Icon className="w-3.5 h-3.5" /> {detail.audience_label}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-semibold leading-[1.05]">{service.name}</h1>
          <p className="mt-6 text-lg text-[#374151] leading-relaxed max-w-3xl">{service.long}</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to={detail.cta_primary.to}><Button className="btn-primary-navy rounded-full h-12 px-6" data-testid={`service-${service.slug}-cta-primary`}>{detail.cta_primary.label} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to={detail.cta_secondary.to}><Button variant="outline" className="rounded-full h-12 px-6">{detail.cta_secondary.label}</Button></Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="uppercase-label mb-5">What's included</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Highlights of this product.</h2>
            <ul className="mt-8 space-y-4">
              {detail.bullets.map((b) => (
                <li key={b.text || b} className="flex items-start gap-3 text-[#374151] leading-relaxed">
                  <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                  {b.to ? <Link to={b.to} className="underline underline-offset-4 hover:text-[#0066FF]" data-testid="service-states-link">{b.text}</Link> : b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="uppercase-label mb-5">Eligibility</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Who this is for.</h2>
            <div className="mt-8 bg-[#0A2540] text-white rounded-2xl p-8 space-y-4">
              {detail.eligibility.map((e, i) => (
                <div key={e} className="flex items-start gap-3">
                  <div className="font-display text-lg font-semibold text-[#0066FF] tabular-nums w-6">{i + 1}.</div>
                  <div className="text-white/90 leading-relaxed">{e}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Long intro */}
      {service.long_intro && (
        <section className="py-14 lg:py-16 border-t border-[#E5E7EB]">
          <div className="mx-auto max-w-4xl px-6 lg:px-10">
            <div className="uppercase-label mb-4">Overview</div>
            <p className="text-lg text-[#374151] leading-relaxed">{service.long_intro}</p>
          </div>
        </section>
      )}

      {/* Who it's for + How this service works */}
      {service.who_its_for && (
        <section className="py-14 lg:py-16 bg-[#F9FAFB] border-y border-[#E5E7EB]">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
            <div>
              <div className="uppercase-label mb-3">Who this is for</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Best fit for these situations.</h2>
              <ul className="mt-6 space-y-3">
                {service.who_its_for.map((w) => (
                  <li key={w} className="flex items-start gap-3 p-3 bg-white border border-[#E5E7EB] rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-[#0066FF] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] text-sm">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="uppercase-label mb-3">How it works</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">The process.</h2>
              <p className="mt-6 text-[#374151] leading-relaxed">{service.how_this_service_works}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/how-it-works" className="text-sm text-[#0066FF] hover:underline font-medium">Full funding process →</Link>
                <Link to="/tools/funding-cost-calculator" className="text-sm text-[#0066FF] hover:underline font-medium">Estimate your cost →</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Per-service FAQ */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="py-16 lg:py-20 bg-white">
          <div className="mx-auto max-w-4xl px-6 lg:px-10">
            <div className="uppercase-label mb-3">Common questions</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Questions we hear about {service.name.toLowerCase()}.</h2>
            <div className="mt-8 space-y-4">
              {service.faqs.map((f, i) => (
                <div key={i} className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg" data-testid={`service-${service.slug}-faq-${i}`}>
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-display font-semibold text-[#0A2540]">{f.q}</div>
                      <p className="mt-2 text-sm text-[#374151] leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/faq" className="mt-6 inline-block text-sm text-[#0066FF] hover:underline font-medium">See the full FAQ →</Link>
          </div>
        </section>
      )}

      {/* Related accidents + cities */}
      {(relatedAccidents.length > 0 || relatedCities.length > 0) && (
        <section className="py-16 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-2 gap-10">
            {relatedAccidents.length > 0 && (
              <div>
                <div className="uppercase-label mb-3">Related case types</div>
                <h2 className="font-display text-2xl text-[#0A2540] font-semibold">Accident types we fund with this service</h2>
                <ul className="mt-6 space-y-2">
                  {relatedAccidents.map((a) => (
                    <li key={a.slug}>
                      <Link to={`/accidents/${a.slug}`} className="block bg-white border border-[#E5E7EB] hover:border-[#0066FF] rounded-lg p-4 transition-colors" data-testid={`service-${service.slug}-accident-${a.slug}`}>
                        <div className="font-display font-semibold text-[#0A2540]">{a.label}</div>
                        <div className="text-sm text-[#6B7280] mt-1">{a.plural}</div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {relatedCities.length > 0 && (
              <div>
                <div className="uppercase-label mb-3"><MapPin className="w-3.5 h-3.5 inline mb-0.5" /> Ohio coverage</div>
                <h2 className="font-display text-2xl text-[#0A2540] font-semibold">Cities where we deliver this service</h2>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {relatedCities.map((c) => (
                    <Link key={c.slug} to={`/states/ohio/${c.slug}`} className="bg-white border border-[#E5E7EB] hover:border-[#0066FF] rounded-lg px-4 py-3 transition-colors" data-testid={`service-${service.slug}-city-${c.slug}`}>
                      <div className="font-display font-semibold text-[#0A2540] text-sm">{c.name}</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">{c.courts}</div>
                    </Link>
                  ))}
                </div>
                <Link to="/states/ohio" className="mt-4 inline-block text-sm text-[#0066FF] hover:underline font-medium">All Ohio pages →</Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Other services */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Other services</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.filter((s) => s.slug !== service.slug).map((s) => {
              const SIcon = ICONS[s.slug] || Briefcase;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="bg-white border border-[#E5E7EB] rounded-xl p-6 hover:border-[#0066FF] hover:shadow-sm transition-all group" data-testid={`other-service-${s.slug}`}>
                  <SIcon className="w-5 h-5 text-[#0066FF]" />
                  <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">{s.name}</h3>
                  <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{s.short}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-[#0066FF] text-sm font-medium">
                    Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold">Ready to get started?</h2>
          <p className="mt-3 text-white/70">Whether you have questions or you're ready to apply — we usually respond within an hour.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to={detail.cta_primary.to}><Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-12 px-7">{detail.cta_primary.label}</Button></Link>
            <Link to="/faq"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A2540] rounded-full h-12 px-7">Read FAQ</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
